import { ValidationError } from '@sequencehq/utils'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import { useEffect } from 'react'

/**
 * Hands a price form's validation state back to the editor. Where the errors are
 * matters as well as whether there are any: saving opens the "Show additional
 * fields" section when the only thing stopping it is hidden down there.
 *
 * `additionalFields` is the form's own list of the properties it renders below
 * `<ShowAdditionalFieldsToggle />`, so the list lives next to the JSX it
 * describes. A property belongs in it because it sits in the section, not
 * because it has a validator today. Pass an empty array for a form with no such
 * section. The external ledger fields are down there too, but they are a
 * sub-form of their own with its own validity flag.
 *
 * Reading the form's `queries` rather than taking its `onValidationStateChange`
 * callback is deliberate. That callback only fires when the overall result
 * flips, so a form that was already invalid never reports the extra field it has
 * since tripped over - which is exactly the case this needs to catch.
 */
export const useSyncFormValidity = ({
  isFormValid,
  formValidationResults,
  additionalFields
}: {
  isFormValid: boolean
  formValidationResults: ValidationError[]
  additionalFields: string[]
}) => {
  const pricingEditorContext = usePricingEditorDomainContext()
  const { updateEditor } = pricingEditorContext.mutators.common

  const additionalFieldsValid = !formValidationResults.some(error =>
    additionalFields.includes(error.fieldName)
  )

  /**
   * The two booleans we write are the whole output of this effect, so they are
   * the whole dependency list. `additionalFields` and `formValidationResults`
   * are deliberately left out even though the booleans derive from them: both
   * are rebuilt on every render, so depending on either would dispatch every
   * render, and each dispatch re-renders the editor - a loop.
   */
  useEffect(() => {
    updateEditor({
      valid: isFormValid,
      additionalFieldsValid
    })
  }, [additionalFieldsValid, isFormValid, updateEditor])
}
